import { toast, type ToastOptions } from 'vue3-toastify'
import type {
  EventDate,
  EventDescription,
  Price,
} from '~/composables/types/event'
import { useNotificationsStore } from '../../stores/notifications.ts'

interface CartItemUpdateData {
  item: {
    price: Price
    original_price: Price
    id: string
  }
}

interface NotPublicErrorData {
  date: {
    id: string
  }
}

interface MinAdvanceErrorData {
  date: {
    id: string
  }
}

type SoldOutError = {
  reason: 'soldOut'
  event: Pick<EventDescription, 'id' | 'permalink' | 'title'>
  date: Pick<EventDate, 'end' | 'start' | 'id'>
  message: string
  code: number
}

type NotAvailableError = {
  reason: 'notAvailable'
  event: Pick<EventDescription, 'id' | 'permalink' | 'title'>
  date: Pick<EventDate, 'end' | 'start' | 'id'>
  message: string
}

type ValidationError =
  | {
      reason:
        | 'cartItemWasUpdated'
        | 'notPublic'
        | 'RequiredAgeOfMajority'
        | 'minAdvance'
        | string
      message?: string
      data?: CartItemUpdateData | NotPublicErrorData
    }
  | NotAvailableError

interface ValidationErrorResponse {
  data: {
    reason: string
    errors: ValidationError[]
    message?: string
  }
}

export const useCartErrorHandlers = () => {
  const { cart, removeItem, updateItemPricings } = useCartWithSubdomainLogic()
  const requireAgeOfMajority = ref(false)
  const { addNotification } = useNotificationsStore()

  const handleCartItemWasUpdated = async (data: CartItemUpdateData) => {
    if (!data?.item) return
    const price = data.item.price
    const id = data.item.id

    const outdatedItem = cart.value.items.find((item) => item.id === id)

    if (!outdatedItem) return

    await updateItemPricings(outdatedItem)

    outdatedItem.wasUpdated = true
  }

  const handleNotPublicError = (data: NotPublicErrorData) => {
    const item = cart.value.items.find((x) => data?.date.id === x.id)
    if (!item) return
    removeItem(item)
  }

  const handleMinAdvanceError = (data: MinAdvanceErrorData) => {
    const item = cart.value.items.find((x) => data?.date.id === x.id)
    if (!item) return
    removeItem(item)
  }

  const handleRequireAgeOfMajorityError = () => {
    requireAgeOfMajority.value = true
  }

  const handleRequestValidation = (e: ValidationErrorResponse) => {
    const errors = e.data.errors

    if (!errors) return

    for (const [key, value] of Object.entries(errors)) {
      const itemIndex = key.split('.')[1]
      const item = cart.value.items[Number(itemIndex)]
      if (!item) continue

      cart.value.items.splice(Number(itemIndex), 1)
      toast(value[0], {
        autoClose: 2000,
        type: 'error',
        position: toast.POSITION.TOP_CENTER,
        multiple: false,
      } as ToastOptions)
    }
  }

  const handleCartItemGeneralError = (e: ValidationError) => {
    addNotification({
      message: e.message || 'An error occurred',
      type: 'ERROR',
    })
  }

  const handleNotAvailableError = (e: NotAvailableError) => {
    const item = cart.value.items.find((x) => e.date?.id === x.id)

    if (item) {
      removeItem(item)
    }

    addNotification({
      message: e.message,
      type: 'ERROR',
      persist: true,
    })
  }

  const handleSoldOutError = (e: SoldOutError) => {
    const item = cart.value.items.find((x) => e.date?.id === x.id)

    if (item) {
      removeItem(item)
    }

    addNotification({
      message: e.message,
      type: 'ERROR',
      persist: true,
    })
  }

  const handleValidationErrors = (e: ValidationErrorResponse) => {
    if (!e?.data) return

    if (e.data.reason === 'requestValidation') {
      handleRequestValidation(e)
      return
    }

    const itemErrorHandlers = {
      notFound: handleNotAvailableError,
      cartItemWasUpdated: handleCartItemWasUpdated,
      notPublic: handleNotPublicError,
      RequiredAgeOfMajority: handleRequireAgeOfMajorityError,
      productNotAllowed: handleNotAvailableError,
      validationFailed: handleNotAvailableError,
      notAvailable: handleNotAvailableError,
      minAdvance: handleMinAdvanceError,
      soldOut: handleSoldOutError,
    }

    if (e.data.reason === 'cartItemsValidation' && e.data.errors) {
      e.data.errors.forEach((err: Partial<ValidationError>) => {
        const reason = err.reason
        const handler =
          itemErrorHandlers[reason as keyof typeof itemErrorHandlers]

        if (handler) {
          Promise.resolve(handler(err as any))
        } else {
          handleCartItemGeneralError(err as any)
        }
      })
    }
  }

  return {
    requireAgeOfMajority,
    handleValidationErrors,
  }
}
